/**
 * Team onboarding page.
 *
 * Portal-owner-only. Lets the owner invite agents to the XyloTrust training
 * (POST → backend sends branded SES mail with opaque-token training URL),
 * shows team progress (passed / pending), and lets the owner remove agents.
 *
 * Mirrors the standalone "Team onboarding" mock: stats strip, 2-column
 * layout with invite form on the left + status table on the right.
 */
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Send, UserPlus, Trash2, Mail, CheckCircle2, Clock } from 'lucide-react';
import {
  fetchTeamAgents,
  inviteTeamAgents,
  deleteTeamAgent,
  type TeamAgent,
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useLang } from '../lib/i18n';
import { cn } from '../lib/utils';

function dateShort(iso: string, lang: 'nl' | 'en'): string {
  try {
    return new Date(iso).toLocaleDateString(lang === 'nl' ? 'nl-BE' : 'en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch { return iso; }
}

function parseEmails(raw: string): string[] {
  return (raw.match(/[^\s,;]+@[^\s,;]+\.[^\s,;]+/g) ?? [])
    .map(s => s.toLowerCase());
}

export function TeamOnboarding() {
  const qc        = useQueryClient();
  const { t, lang } = useLang();
  const tr        = t.team;

  const [raw,    setRaw]    = useState('');
  const [toast,  setToast]  = useState<string | null>(null);
  const [modal,  setModal]  = useState<{ to: string; count: number } | null>(null);
  const toastTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2400);
  };

  const { data: agents = [], isLoading, error } = useQuery({
    queryKey: ['team-agents'],
    queryFn:  fetchTeamAgents,
  });

  const inviteMut = useMutation({
    mutationFn: (emails: string[]) => inviteTeamAgents(emails, lang),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['team-agents'] });
      setRaw('');
      const anyFailed = data.results.some(r => r.status === 'send_failed');
      const msg =
        anyFailed                     ? tr.toast.sendFailed
        : data.skipped > 0             ? tr.toast.invitedSome.replace('{added}', String(data.added)).replace('{skipped}', String(data.skipped))
        :                                tr.toast.invited;
      showToast(msg);
      // Show summary modal — mirrors the standalone UX without faking a
      // training URL (the actual opaque-token link lives only in the mail).
      const addedList = data.results.filter(r => r.status === 'added').map(r => r.email);
      if (addedList.length > 0) {
        setModal({ to: addedList[0], count: addedList.length });
      }
    },
    onError: () => showToast(tr.invite.invalidError),
  });

  const deleteMut = useMutation({
    mutationFn: deleteTeamAgent,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team-agents'] });
      showToast(tr.toast.removed);
    },
  });

  const stats = useMemo(() => {
    const total  = agents.length;
    const passed = agents.filter((a: TeamAgent) => a.passed).length;
    return { total, passed, pending: total - passed };
  }, [agents]);

  const submit = () => {
    const txt = raw.trim();
    if (!txt) { showToast(tr.invite.emptyError); return; }
    const emails = parseEmails(txt);
    if (!emails.length) { showToast(tr.invite.invalidError); return; }
    inviteMut.mutate(emails);
  };

  const remove = (email: string) => {
    if (!window.confirm(tr.table.confirmRemove)) return;
    deleteMut.mutate(email);
  };

  return (
    <div className="max-w-7xl">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">{tr.title}</h1>
        <p className="text-sm text-gray-500 mt-1">{tr.subtitle}</p>
      </header>

      <div className="grid gap-5 lg:grid-cols-[380px_1fr] items-start">

        {/* ── INVITE CARD ─────────────────────────────────────────────── */}
        <Card>
          <CardHeader className="flex items-center gap-2 px-6 py-4 border-b border-gray-100 flex-row space-y-0">
            <Send className="h-4 w-4 text-brand-600" />
            <CardTitle>{tr.invite.title}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <label htmlFor="emails" className="block text-sm font-medium text-gray-700">
              {tr.invite.label}
            </label>
            <textarea
              id="emails"
              value={raw}
              onChange={e => setRaw(e.target.value)}
              placeholder={tr.invite.placeholder}
              rows={5}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm
                         font-mono leading-relaxed
                         focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 leading-relaxed">{tr.invite.hint}</p>
            <Button
              onClick={submit}
              loading={inviteMut.isPending}
              className="w-full">
              <Send className="h-4 w-4" />
              {inviteMut.isPending ? tr.invite.sending : tr.invite.cta}
            </Button>
          </CardContent>
        </Card>

        {/* ── STATS + TABLE ───────────────────────────────────────────── */}
        <div className="space-y-4">
          {/* Stats strip */}
          <div className="grid grid-cols-3 bg-white border border-gray-200 rounded-xl overflow-hidden">
            <Stat value={stats.total}   label={tr.stats.total}   />
            <Stat value={stats.passed}  label={tr.stats.passed}  tone="green" border />
            <Stat value={stats.pending} label={tr.stats.pending} tone="amber" />
          </div>

          {/* Table card */}
          <Card>
            <CardHeader className="flex items-center gap-2 px-6 py-4 border-b border-gray-100 flex-row space-y-0">
              <UserPlus className="h-4 w-4 text-brand-600" />
              <CardTitle>{tr.table.title}</CardTitle>
            </CardHeader>

            {error && (
              <div className="px-6 py-8 text-sm text-red-600 text-center">{tr.invite.loadError}</div>
            )}

            {!error && !isLoading && agents.length === 0 && (
              <div className="px-6 py-10 text-sm text-gray-500 text-center">{tr.table.empty}</div>
            )}

            {!error && agents.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 border-b border-gray-100">
                      <th className="px-6 py-3">{tr.table.colAgent}</th>
                      <th className="px-6 py-3">{tr.table.colStatus}</th>
                      <th className="px-6 py-3">{tr.table.colInvited}</th>
                      <th className="px-6 py-3">{tr.table.colCompleted}</th>
                      <th className="px-6 py-3 text-right" aria-label="actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {agents.map((a: TeamAgent) => (
                      <tr key={a.email} className="border-b border-gray-50 last:border-b-0">
                        <td className="px-6 py-3 font-medium text-gray-900">{a.email}</td>
                        <td className="px-6 py-3">
                          {a.passed
                            ? <Badge tone="green" icon={<CheckCircle2 className="h-3 w-3" />}>{tr.table.badgePassed}</Badge>
                            : <Badge tone="amber" icon={<Clock         className="h-3 w-3" />}>{tr.table.badgePending}</Badge>}
                        </td>
                        <td className="px-6 py-3 text-gray-500 text-xs">{dateShort(a.invited_at, lang)}</td>
                        <td className="px-6 py-3 text-gray-500 text-xs">
                          {a.passed_at ? dateShort(a.passed_at, lang) : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-6 py-3 text-right">
                          <button
                            onClick={() => remove(a.email)}
                            disabled={deleteMut.isPending}
                            className="text-xs text-gray-500 hover:text-red-600 font-medium inline-flex items-center gap-1.5
                                       disabled:opacity-50">
                            <Trash2 className="h-3 w-3" /> {tr.table.remove}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* ── Invite-sent modal ───────────────────────────────────────────── */}
      {modal && (
        <div
          onClick={() => setModal(null)}
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/55 backdrop-blur-sm p-5"
        >
          <div
            onClick={e => e.stopPropagation()}
            className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-7 space-y-4"
          >
            <h3 className="text-lg font-bold text-gray-900">{tr.modal.title}</h3>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2 text-sm">
              <p className="text-gray-500">
                <strong className="text-gray-800">{tr.modal.to}</strong>{' '}
                {modal.to}{modal.count > 1 ? ` (+${modal.count - 1})` : ''}
              </p>
              <p className="text-gray-500"><strong className="text-gray-800">{tr.modal.subject}</strong> {tr.modal.subjectValue}</p>
              <p className="text-gray-700 leading-relaxed pt-2">{tr.modal.body}</p>
              <div className="pt-2">
                <span className="inline-block bg-brand-600 text-white text-xs font-semibold px-4 py-2 rounded-lg">
                  {tr.modal.cta}
                </span>
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={() => setModal(null)}>{tr.modal.close}</Button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-sm font-medium
                        px-5 py-3 rounded-full shadow-xl z-50 flex items-center gap-2">
          <Mail className="h-4 w-4 text-green-400" />
          {toast}
        </div>
      )}
    </div>
  );
}

// ── Small bits ─────────────────────────────────────────────────────────────
function Stat({ value, label, tone, border }: { value: number; label: string; tone?: 'green' | 'amber'; border?: boolean }) {
  const color = tone === 'green' ? 'text-green-600' : tone === 'amber' ? 'text-amber-600' : 'text-gray-900';
  return (
    <div className={cn('px-5 py-4', border && 'border-x border-gray-100')}>
      <div className={cn('text-2xl font-bold tracking-tight', color)}>{value}</div>
      <div className="text-xs text-gray-500 mt-0.5">{label}</div>
    </div>
  );
}

function Badge({ tone, icon, children }: { tone: 'green' | 'amber'; icon: React.ReactNode; children: React.ReactNode }) {
  const cls = tone === 'green'
    ? 'bg-green-100 text-green-700'
    : 'bg-amber-100 text-amber-700';
  return (
    <span className={cn('inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full', cls)}>
      {icon}{children}
    </span>
  );
}
